import { supabase } from "@/integrations/supabase/client";

type ErrorType =
  | "window_error"
  | "unhandled_rejection"
  | "react_boundary"
  | "manual";

interface CrashReport {
  errorType: ErrorType;
  message: string;
  stack?: string;
  componentStack?: string;
  metadata?: Record<string, unknown>;
}

// Throttle: skip duplicate identical errors within this window
const DEDUPE_WINDOW_MS = 10_000;
const recent = new Map<string, number>();

function shouldSkip(key: string): boolean {
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return true;
  recent.set(key, now);
  // Cap memory: keep last 50 keys
  if (recent.size > 50) {
    const oldest = recent.keys().next().value;
    if (oldest) recent.delete(oldest);
  }
  return false;
}

function detectPlatform(): string {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Windows/i.test(ua)) return "windows";
  if (/Macintosh|Mac OS X/i.test(ua)) return "macos";
  if (/Linux/i.test(ua)) return "linux";
  return "unknown";
}

export async function reportCrash(report: CrashReport): Promise<void> {
  try {
    // Filter known noise that is NOT actionable:
    // 1. Browser extensions (e.g. privacy/security tools) intercepting service
    //    worker registration and rejecting it. Identified by `wrsParams` or
    //    other extension-injected wrappers in the stack.
    // 2. Vite HMR/fast-refresh churn in the dev preview where stale component
    //    references briefly lose the AuthContext. Never reproduces in
    //    production builds.
    const stack = report.stack ?? "";
    const url = typeof window !== "undefined" ? window.location.href : "";
    const isExtensionSWNoise =
      /wrsParams|chrome-extension:|moz-extension:|safari-extension:/i.test(stack) ||
      (/registerSW\.js/i.test(stack) && /Rejected/i.test(report.message));
    const isDevHmrAuthNoise =
      report.message === "useAuth must be used within an AuthProvider" &&
      /lovableproject\.com|localhost/i.test(url);
    if (isExtensionSWNoise || isDevHmrAuthNoise) return;

    const dedupeKey = `${report.errorType}:${report.message}`;
    if (shouldSkip(dedupeKey)) return;

    let userId: string | null = null;
    try {
      const { data } = await supabase.auth.getUser();
      userId = data.user?.id ?? null;
    } catch {
      // Ignore — auth may be broken; we still want the crash logged
    }

    await supabase.from("client_error_logs").insert([
      {
        user_id: userId ?? undefined,
        error_type: report.errorType,
        message: report.message || "(no message)",
        stack: report.stack,
        component_stack: report.componentStack,
        url: window.location.href,
        user_agent: navigator.userAgent,
        app_version: (import.meta.env.VITE_APP_VERSION as string | undefined),
        platform: detectPlatform(),
        metadata: (report.metadata ?? null) as never,
      },
    ]);
  } catch {
    // Never let crash reporting itself crash the app
  }
}

let installed = false;

/**
 * Install global crash listeners (window.onerror + unhandledrejection).
 * Safe to call multiple times.
 */
export function installCrashReporter(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    void reportCrash({
      errorType: "window_error",
      message: event.message || event.error?.message || "Unknown error",
      stack: event.error?.stack,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      typeof reason === "string"
        ? reason
        : reason?.message ?? String(reason ?? "Unhandled rejection");
    void reportCrash({
      errorType: "unhandled_rejection",
      message,
      stack: reason?.stack,
    });
  });
}